import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

export interface ProviderCall {
  step?: string;
  provider?: string;
  url?: string;
  request?: Record<string, unknown>;
  response?: Record<string, unknown>;
}

export interface ProviderPayload {
  provider?: string;
  url?: string;
  request?: Record<string, unknown>;
  response?: Record<string, unknown>;
  calls?: ProviderCall[];
}

export interface RequestLog {
  id: string;
  created_at: string;
  function_name: string;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  user_id: string | null;
  request_body: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  error: string | null;
  ip: string | null;
  provider_payload?: ProviderPayload | null;
  user_email?: string | null;
  user_display_name?: string | null;
}

export interface LogsFilter {
  function_name?: string;
  status_class?: "2xx" | "4xx" | "5xx";
  search?: string;
  limit?: number;
}

type Client = SupabaseClient<Database>;

export async function fetchRequestLogs(client: Client, filter: LogsFilter = {}): Promise<RequestLog[]> {
  let query = client
    .from("request_logs")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(filter.limit ?? 200);

  if (filter.function_name) {
    query = query.eq("function_name", filter.function_name);
  }

  if (filter.status_class === "2xx") {
    query = query.gte("status", 200).lt("status", 300);
  } else if (filter.status_class === "4xx") {
    query = query.gte("status", 400).lt("status", 500);
  } else if (filter.status_class === "5xx") {
    query = query.gte("status", 500).lt("status", 600);
  }

  const { data, error } = await query;
  if (error) throw error;

  const logs = (data ?? []) as RequestLog[];

  // --- llm-proxy: join ai_job_results by job_id ---
  const jobIds = logs
    .map((l) => (l.metadata as Record<string, unknown> | null)?.job_id as string | undefined)
    .filter((id): id is string => Boolean(id));

  const payloadByJobId = new Map<string, ProviderPayload | null>();
  if (jobIds.length > 0) {
    const { data: results } = await client
      .from("ai_job_results")
      .select("job_id, raw_provider_payload")
      .in("job_id", jobIds);
    if (results) {
      for (const r of results) {
        payloadByJobId.set(r.job_id as string, r.raw_provider_payload as ProviderPayload | null);
      }
    }
  }

  // --- simulator: join simulator_messages by session_id ---
  const sessionIds = logs
    .filter((l) => l.function_name === "simulator")
    .map((l) => (l.metadata as Record<string, unknown> | null)?.session_id as string | undefined)
    .filter((id): id is string => Boolean(id));

  const callsBySessionId = new Map<string, ProviderCall[]>();
  if (sessionIds.length > 0) {
    const { data: msgs } = await client
      .from("simulator_messages")
      .select("session_id, raw_provider_payload, created_at")
      .in("session_id", sessionIds)
      .order("created_at", { ascending: true });
    if (msgs) {
      for (const m of msgs) {
        const sid = m.session_id as string;
        const payload = m.raw_provider_payload as { calls?: ProviderCall[] } | null;
        if (!payload?.calls?.length) continue;
        const existing = callsBySessionId.get(sid) ?? [];
        callsBySessionId.set(sid, [...existing, ...payload.calls]);
      }
    }
  }

  // --- Join profiles: user_id (Clerk id, text) → email + display_name ---
  // request_logs.user_id stores Clerk user_xxx strings, while profiles.id is
  // the internal uuid. The bridge column is profiles.clerk_id.
  const userIds = Array.from(
    new Set(logs.map((l) => l.user_id).filter((id): id is string => Boolean(id))),
  );

  const profileByUserId = new Map<string, { email: string | null; display_name: string | null }>();
  if (userIds.length > 0) {
    const { data: profiles } = await client
      .from("profiles")
      .select("clerk_id, email, display_name")
      .in("clerk_id", userIds);
    if (profiles) {
      for (const p of profiles) {
        const clerkId = (p as { clerk_id: string | null }).clerk_id;
        if (!clerkId) continue;
        profileByUserId.set(clerkId, {
          email: (p.email as string | null) ?? null,
          display_name: (p.display_name as string | null) ?? null,
        });
      }
    }
  }

  return logs.map((log) => {
    const meta = log.metadata as Record<string, unknown> | null;
    const jobId = meta?.job_id as string | undefined;
    const sessionId = meta?.session_id as string | undefined;
    const profile = log.user_id ? profileByUserId.get(log.user_id) : undefined;
    const enriched: RequestLog = {
      ...log,
      user_email: profile?.email ?? null,
      user_display_name: profile?.display_name ?? null,
    };

    if (jobId && payloadByJobId.has(jobId)) {
      return { ...enriched, provider_payload: payloadByJobId.get(jobId) ?? null };
    }
    if (sessionId && callsBySessionId.has(sessionId)) {
      return { ...enriched, provider_payload: { calls: callsBySessionId.get(sessionId)! } };
    }
    return enriched;
  });
}

export async function clearOldLogs(client: Client, olderThanDays = 30): Promise<number> {
  const cutoff = new Date(Date.now() - olderThanDays * 86400_000).toISOString();
  const { count, error } = await client
    .from("request_logs")
    .delete({ count: "exact" })
    .lt("created_at", cutoff);
  if (error) throw error;
  return count ?? 0;
}
